import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button, Col, Row, Spin } from 'antd';

import { useAppDispatch } from 'hooks/useAppDispatch';
import { getCompanyDetail } from 'redux/reducers/workerSlice';
import BaseAlert from 'components/wrappers/BaseAlert';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { useAppSelector } from 'hooks/useAppSelector';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import BasePageLabel from 'components/wrappers/BasePageLabel';
import ActivitiesDetailsSection from 'components/ActivitiesDetailsSection';
import { ActivitiesSectionPageTypeEnum } from 'components/ActivitiesDetailsSection/enums';
import { CompanyType } from './types/CompanyType';
import { ContractorCompanyDetailAdminViewModel } from 'api/adminApiClient';
import BasePageCard from 'components/wrappers/BasePageCard';
import { getFullName } from 'utils/personUtils';
import CompanyGeneralInfo from './CompanyGeneralInfo';
import CompanyModal from 'components/modals/CompanyModal';

type ParamsProps = {
  companyId: string;
};

export default function CompanyDetailsPage() {
  const { companyId } = useParams<ParamsProps>();
  const [company, setCompany] = useState<CompanyType>();
  const [isInitialLoaded, setIsInitialLoaded] = useState(false);
  const [isCompanyEditModalOpen, setIsCompanyEditModalOpen] = useState(false);
  const isAdmin = useAppSelector(isAdminSelector);
  const dispatch = useAppDispatch();
  const containerRef = useRef<HTMLDivElement>(null);

  const loadCompanyDetails = useCallback(async () => {
    if (!companyId) return;

    const result = await dispatch(
      getCompanyDetail({ companyId, isAdminRole: isAdmin }),
    );

    if (getCompanyDetail.fulfilled.match(result)) {
      setCompany(result.payload);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }

    setIsInitialLoaded(true);
  }, [dispatch, companyId, isAdmin]);

  useEffect(() => {
    loadCompanyDetails();
  }, [loadCompanyDetails]);

  if (!isInitialLoaded) return <Spin size="large" />;

  if (!company) return null;

  const { companyName, id, createdBy } = company;

  const referredMembers = (company as ContractorCompanyDetailAdminViewModel)
    ?.referredMembers;
  const referredAssignments = (company as ContractorCompanyDetailAdminViewModel)
    ?.referredAssignments;

  const handleCompanyEditModalOpen = () => {
    setIsCompanyEditModalOpen(true);
  };

  const handleCompanyEditModalClose = () => {
    setIsCompanyEditModalOpen(false);
  };

  return (
    <>
      {isCompanyEditModalOpen && (
        <CompanyModal
          companyDetails={company}
          onClose={handleCompanyEditModalClose}
          onSuccess={loadCompanyDetails}
        />
      )}
      <div ref={containerRef} className="page-wrapper scroll-box">
        <div className="page-holder">
          <BasePageLabel
            label="Contractor companies Details"
            creator={createdBy}
          />

          <div className="page-box page-inner">
            <div className="page-inner-container">
              <div className="page-head">
                <div className="page-head__left">
                  <div className="page-title page-head__title">
                    {companyName}
                  </div>
                </div>
                <Button onClick={handleCompanyEditModalOpen}>Edit</Button>
              </div>
              <Row gutter={[10, 10]}>
                <Col xs={24} lg={12} className="d-flex">
                  <Row gutter={[4, 4]}>
                    <Col xs={24} className="d-flex">
                      <BasePageCard
                        title={
                          isAdmin
                            ? 'Current company record'
                            : 'General information'
                        }
                        icons={<span className="icon icon-writing-tool-user" />}
                        className="stretch-card"
                      >
                        <div className="content-wrapper">
                          <Row gutter={[4, 4]} className="titled-sheet">
                            <CompanyGeneralInfo company={company} />
                          </Row>
                        </div>
                      </BasePageCard>
                    </Col>

                    {isAdmin &&
                      (!!referredMembers?.length ||
                        !!referredAssignments?.length) && (
                        <Col xs={24} className="d-flex">
                          <BasePageCard
                            title="Refers to"
                            icons={<span className="icon icon-refers-to" />}
                            className="stretch-card"
                          >
                            <div className="content-wrapper">
                              <Row gutter={[4, 4]} className="titled-sheet">
                                {!!referredMembers?.length && (
                                  <Col xs={24} md={6}>
                                    <div className="title">Members:</div>
                                    {referredMembers
                                      .map<React.ReactNode>((member) => (
                                        <Link
                                          to={`/member/${member.memberKey}`}
                                          key={`reffered-member-${member.memberKey}`}
                                        >
                                          {getFullName(
                                            member.firstName,
                                            member.lastName,
                                          )}
                                        </Link>
                                      ))
                                      .reduce((previous, current) => [
                                        previous,
                                        ', ',
                                        current,
                                      ])}
                                  </Col>
                                )}

                                {!!referredAssignments?.length && (
                                  <Col xs={24} md={6}>
                                    <div className="title">Assignments:</div>
                                    {referredAssignments
                                      ?.map<React.ReactNode>((assignment) => (
                                        <Link
                                          to={`/assignment/${assignment.assignmentKey}`}
                                          key={`referred-assignment-${assignment.assignmentKey}`}
                                        >
                                          {assignment.assignmentKey}
                                        </Link>
                                      ))
                                      .reduce((previous, current) => [
                                        previous,
                                        ', ',
                                        current,
                                      ])}
                                  </Col>
                                )}
                              </Row>
                            </div>
                          </BasePageCard>
                        </Col>
                      )}
                  </Row>
                </Col>
              </Row>
            </div>
            {isAdmin && (
              <ActivitiesDetailsSection
                entityId={id as string}
                pageType={ActivitiesSectionPageTypeEnum.Company}
                scrollRef={containerRef}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}
